import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Endpoints } from '@xver/apis';
import type {
    ApiError,
    ProjectRequestsModel,
    TransferAcceptResponse,
} from '@xver/apis/v1/projects/model';
import { Button } from '@xver/components/Atoms';
import { useAppSelector } from '@xver/hooks/useAppSelector';
import { invalidateProjectQueries, useProjectFeedback } from '@xver/hooks/useProjectQueries';
import { useProjectsQuery } from '@xver/hooks/useProjectsQuery';
import { formatDateTime } from '@xver/utils/date';
import { getErrorCode } from '@xver/utils/error';
import { focusAfterRender, transferKeepsPayer, transferNotices } from '@xver/utils/projects';
import { ReactNode, RefObject, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AccessSummary } from './ProjectBits';

type RequestAction =
    | { kind: 'invitation'; decision: 'accept' | 'decline'; id: number; name: string }
    | { kind: 'payer'; decision: 'accept' | 'decline'; id: string; name: string }
    | { kind: 'ownership'; decision: 'accept' | 'decline'; id: string; name: string };

const requestKey = (action: Pick<RequestAction, 'kind' | 'id'>) => `${action.kind}:${action.id}`;

function run(action: RequestAction): Promise<unknown> {
    const projects = Endpoints.v1.Projects;
    const accept = action.decision === 'accept';
    if (action.kind === 'invitation') {
        return accept
            ? projects.acceptInvitation(action.id)
            : projects.declineInvitation(action.id);
    }
    if (action.kind === 'payer') {
        return accept ? projects.acceptPayer(action.id) : projects.declinePayer(action.id);
    }
    return accept ? projects.acceptTransfer(action.id) : projects.declineTransfer(action.id);
}

const HEADING_ID = 'project-requests-heading';

interface RequestCardProps {
    focusKey: string;
    title: string;
    children: ReactNode;
    projectName: string;
    pending?: 'accept' | 'decline';
    disabled: boolean;
    error?: ReactNode;
    acceptLabel: string;
    declineLabel: string;
    onDecide: (decision: 'accept' | 'decline') => void;
}

function RequestCard({
    focusKey,
    title,
    children,
    projectName,
    pending,
    disabled,
    error,
    acceptLabel,
    declineLabel,
    onDecide,
}: RequestCardProps) {
    return (
        <li className="w-full flex flex-col gap-3 p-4 rounded-md border-[0.5px] border-line bg-surface-1">
            <h3
                tabIndex={-1}
                data-request-key={focusKey}
                className="text-base font-bold break-words"
            >
                {title}
            </h3>
            <div className="flex flex-col gap-2 text-sm text-fg/78 max-w-[80ch]">{children}</div>
            {error && (
                <div role="alert" className="text-sm text-fg">
                    {error}
                </div>
            )}
            <div className="flex flex-wrap gap-2">
                <Button
                    onClick={() => onDecide('accept')}
                    isLoading={pending === 'accept'}
                    disabled={disabled && pending !== 'accept'}
                >
                    {acceptLabel}
                    <span className="sr-only"> {projectName}</span>
                </Button>
                <Button
                    variant="secondary"
                    onClick={() => onDecide('decline')}
                    isLoading={pending === 'decline'}
                    disabled={disabled && pending !== 'decline'}
                >
                    {declineLabel}
                    <span className="sr-only"> {projectName}</span>
                </Button>
            </div>
        </li>
    );
}

interface ProjectRequestsSectionProps {
    requests: ProjectRequestsModel;
    /** Focused after the last request is decided and this section unmounts. */
    fallbackFocusRef: RefObject<HTMLElement | null>;
}

export default function ProjectRequestsSection({
    requests,
    fallbackFocusRef,
}: ProjectRequestsSectionProps) {
    const { t, i18n } = useTranslation('projects');
    const queryClient = useQueryClient();
    const viewerId = useAppSelector((state) => state.auth.user?.id);
    const { data: projects } = useProjectsQuery();
    const { errorMessage, notifySuccess } = useProjectFeedback();
    const [errors, setErrors] = useState<Record<string, unknown>>({});

    const decide = useMutation({
        mutationFn: run,
        onMutate: (action) =>
            setErrors((previous) => ({ ...previous, [requestKey(action)]: undefined })),
        onSuccess: async (result, action) => {
            const accepted = action.decision === 'accept';
            if (action.kind === 'ownership' && accepted) {
                const results = (result as TransferAcceptResponse).results ?? [];
                notifySuccess(
                    t('requests.toast.ownership.accept', { name: action.name }),
                    results.length === 0
                        ? t('requests.toast.acceptedHint')
                        : t('move.results.summary', {
                              moved: results.filter((item) => item.status === 'moved').length,
                              skipped: results.filter((item) => item.status === 'skipped').length,
                              failed: results.filter((item) => item.status === 'failed').length,
                          })
                );
            } else {
                notifySuccess(
                    t(`requests.toast.${action.kind}.${action.decision}`, { name: action.name }),
                    accepted ? t('requests.toast.acceptedHint') : t('requests.toast.declinedHint')
                );
            }
            await invalidateProjectQueries(queryClient, {
                withResources: action.kind === 'ownership' && accepted,
            });
            focusAfterRender(() => document.getElementById(HEADING_ID) ?? fallbackFocusRef.current);
        },
        onError: (error, action) => {
            setErrors((previous) => ({ ...previous, [requestKey(action)]: error }));
            focusAfterRender(() =>
                document.querySelector<HTMLElement>(`[data-request-key="${requestKey(action)}"]`)
            );
        },
    });

    const cardState = (action: Pick<RequestAction, 'kind' | 'id'>) => {
        const key = requestKey(action);
        const active = decide.isPending && decide.variables && requestKey(decide.variables) === key;
        const error = errors[key];
        const code = getErrorCode(error);
        const busy =
            code === 'resource_busy' || code === 'image_name_conflict'
                ? (error as ApiError).resources
                : undefined;
        return {
            focusKey: key,
            pending: active ? decide.variables?.decision : undefined,
            disabled: decide.isPending,
            error: error ? (
                <>
                    <p>{errorMessage(error, { self: action.kind === 'ownership' })}</p>
                    {busy && busy.length > 0 && (
                        <ul className="mt-1 list-disc ps-5">
                            {busy.map((item) => (
                                <li key={`${item.type}:${item.id}`}>
                                    {item.name}: {item.reason}
                                </li>
                            ))}
                        </ul>
                    )}
                </>
            ) : undefined,
        };
    };

    const total =
        requests.invitations.length +
        requests.payerRequests.length +
        requests.ownershipOffers.length;

    return (
        <section aria-labelledby={HEADING_ID} className="w-full flex flex-col gap-3">
            <div className="flex flex-col gap-1">
                <h2 id={HEADING_ID} tabIndex={-1} className="text-xl font-bold">
                    {t('requests.title', { count: total })}
                </h2>
                <p className="text-sm text-fg/68">{t('requests.description')}</p>
            </div>
            <ul className="w-full flex flex-col gap-3">
                {requests.invitations.map((invitation) => (
                    <RequestCard
                        key={`invitation-${invitation.id}`}
                        {...cardState({ kind: 'invitation', id: invitation.id })}
                        title={t('requests.invitation.title', {
                            inviter: invitation.inviter.name,
                            name: invitation.project.name,
                        })}
                        projectName={invitation.project.name}
                        acceptLabel={t('requests.invitation.accept')}
                        declineLabel={t('requests.decline')}
                        onDecide={(decision) =>
                            decide.mutate({
                                kind: 'invitation',
                                decision,
                                id: invitation.id,
                                name: invitation.project.name,
                            })
                        }
                    >
                        <p>
                            {t('requests.invitation.role', { role: t(`roles.${invitation.role}`) })}
                            {' · '}
                            <AccessSummary role={invitation.role} access={invitation.access} />
                        </p>
                        <p>{t('requests.invitation.consequence')}</p>
                        <p>
                            {t('requests.expires', {
                                date: formatDateTime(invitation.expiresAt, i18n.language),
                            })}
                        </p>
                    </RequestCard>
                ))}
                {requests.payerRequests.map((request) => (
                    <RequestCard
                        key={`payer-${request.projectId}`}
                        {...cardState({ kind: 'payer', id: request.projectId })}
                        title={t('requests.payer.title', {
                            owner: request.ownerName,
                            name: request.projectName,
                        })}
                        projectName={request.projectName}
                        acceptLabel={t('requests.payer.accept')}
                        declineLabel={t('requests.decline')}
                        onDecide={(decision) =>
                            decide.mutate({
                                kind: 'payer',
                                decision,
                                id: request.projectId,
                                name: request.projectName,
                            })
                        }
                    >
                        <p>{t('requests.payer.consequence')}</p>
                        <p>{t('requests.payer.fallback')}</p>
                    </RequestCard>
                ))}
                {requests.ownershipOffers.map((offer) => {
                    const offered = projects?.find((project) => project.id === offer.projectId);
                    const billing = !offered
                        ? 'unknown'
                        : transferKeepsPayer(offered, viewerId)
                          ? 'payerKeeps'
                          : 'you';
                    const notices = transferNotices(offer.resources);
                    return (
                        <RequestCard
                            key={`ownership-${offer.projectId}`}
                            {...cardState({ kind: 'ownership', id: offer.projectId })}
                            title={t('requests.ownership.title', {
                                from: offer.fromName,
                                name: offer.projectName,
                            })}
                            projectName={offer.projectName}
                            acceptLabel={t('requests.ownership.accept')}
                            declineLabel={t('requests.decline')}
                            onDecide={(decision) =>
                                decide.mutate({
                                    kind: 'ownership',
                                    decision,
                                    id: offer.projectId,
                                    name: offer.projectName,
                                })
                            }
                        >
                            <p>{t('requests.ownership.consequence')}</p>
                            <ul className="list-disc ps-5">
                                <li>
                                    {t('requests.ownership.resources', {
                                        instances: t('counts.instances', {
                                            count: offer.resources.instances,
                                        }),
                                        baremetals: t('counts.baremetals', {
                                            count: offer.resources.baremetals,
                                        }),
                                        containers:
                                            typeof offer.resources.containers === 'number'
                                                ? t('counts.containers', {
                                                      count: offer.resources.containers,
                                                  })
                                                : t('counts.containersUnknown'),
                                    })}
                                </li>
                                <li>{t(`requests.ownership.billing.${billing}`)}</li>
                                {notices.privateIp && <li>{t('move.crossOwner.privateIp')}</li>}
                                {notices.containers && (
                                    <>
                                        <li>
                                            {t('move.crossOwner.registryAccess', {
                                                from: offer.fromName,
                                            })}
                                        </li>
                                        <li>{t('move.crossOwner.credentials')}</li>
                                    </>
                                )}
                                {notices.prepaid && <li>{t('move.crossOwner.prepaid')}</li>}
                            </ul>
                            <p>
                                {t('requests.expires', {
                                    date: formatDateTime(offer.expiresAt, i18n.language),
                                })}
                            </p>
                        </RequestCard>
                    );
                })}
            </ul>
        </section>
    );
}
